import { useEffect } from "react";

import { env } from "@/lib/env";
import { guacHttpClient } from "@/lib/http";
import { searchStore } from "@/stores/search";
import { HttpResponse, StartSearchSessionResponse } from "@/types";

const SEARCH_SESSIONS_PATH = "search-interests/sessions";
// Sessions survive unmount (tab switches within the app) as a paused session
// for up to this long; past it — or after this much inactivity while mounted —
// the session is ended for real.
const SESSION_PAUSE_TIMEOUT_MS = 10 * 60_000;

const INPUT_EVENTS = [
  "keydown",
  "mousemove",
  "pointerdown",
  "scroll",
  "wheel",
] as const;

type SessionEndReason = "inactivity" | "page_hide" | "unmount";

// axios can't fire during page teardown — a keepalive fetch is the only
// reliable way to close the session when the tab closes or navigates away.
const endSessionOnPageHide = (
  sessionId: string,
  idleMs: number,
  reason: SessionEndReason,
) => {
  void fetch(`${env.getGuacBaseUrl()}/${SEARCH_SESSIONS_PATH}`, {
    body: JSON.stringify({ idleMs, reason, sessionId }),
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    keepalive: true,
    method: "PATCH",
  });
};

// Ends whatever session the store holds (live or paused), sends the end call,
// and clears the store. Guac emits the authoritative Mixpanel event.
const endSession = (reason: SessionEndReason) => {
  const state = searchStore.getState();
  const { session, jobId } = state;
  if (!session || !jobId) return;
  const idleMs = state.flushIdleMs();
  if (reason === "page_hide") {
    endSessionOnPageHide(session.id, idleMs, reason);
  } else {
    void guacHttpClient.patch(SEARCH_SESSIONS_PATH, {
      idleMs,
      reason,
      sessionId: session.id,
    });
  }
  state.resetSession();
};

// Starts a search session for the job on mount (or resumes one paused within
// the last 10 minutes) and ends it on tab close (pagehide keepalive) or 10
// minutes of inactivity. Unmount only pauses the session — switching app tabs
// and coming back keeps the same session, with the away time counted as idle.
// Abandoned sessions are still cleaned up server-side on the next start as a
// fallback.
//
// State lives in searchStore; this hook owns only the side effects:
// the HTTP start/end calls, window-level input listeners as the activity
// signal, pagehide, and the pause timer.
export const useSearchSession = (jobId: string) => {
  /* React Hooks */
  const {
    getSessionProgress,
    goals,
    interestCoins,
    interestCount,
    recordInterest,
    recordInterestCoins,
    recordShortlist,
    session,
    shortlists,
    stats,
    status,
  } = searchStore();

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;

    const start = async () => {
      const res = await guacHttpClient.post<
        StartSearchSessionResponse,
        HttpResponse<StartSearchSessionResponse>
      >(SEARCH_SESSIONS_PATH, { jobId });
      if (cancelled) {
        // Unmounted before the start resolved — close it right away.
        void guacHttpClient.patch(SEARCH_SESSIONS_PATH, {
          idleMs: 0,
          reason: "unmount",
          sessionId: res.data.session.id,
        });
        return;
      }
      searchStore.getState().beginSession(jobId, res.data);
    };

    // Resume the paused session for this job if one is waiting. A paused
    // session for a different job is ended immediately; otherwise start fresh.
    const paused = searchStore.getState();
    if (paused.status === "paused" && paused.jobId === jobId) {
      paused.resumeSession();
    } else {
      if (paused.status === "paused") endSession("unmount");
      void start();
    }

    const handlePageHide = () => {
      endSession("page_hide");
    };
    window.addEventListener("pagehide", handlePageHide);

    // Raw window-level input is the activity signal — scrolling, mouse
    // movement, clicks, and typing all keep the idle clock running without
    // callers wiring anything. A gap past the pause timeout means the session
    // is stale — end it and start fresh.
    const handleUserInput = () => {
      const state = searchStore.getState();
      if (state.status !== "live") return;
      const gapMs = state.touchActivity();
      if (gapMs !== null && gapMs > SESSION_PAUSE_TIMEOUT_MS) {
        endSession("inactivity");
        void start();
      }
    };
    INPUT_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleUserInput, {
        capture: true,
        passive: true,
      });
    });

    return () => {
      cancelled = true;
      window.removeEventListener("pagehide", handlePageHide);
      INPUT_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleUserInput, {
          capture: true,
        });
      });
      // Unmount pauses instead of ending: the store keeps the session so a
      // return within the timeout resumes it; the timer ends it otherwise.
      const state = searchStore.getState();
      if (state.status !== "live" || !state.session) return;
      const pausedSessionId = state.session.id;
      state.pauseSession(
        setTimeout(() => {
          const current = searchStore.getState();
          if (
            current.status === "paused" &&
            current.session?.id === pausedSessionId
          ) {
            endSession("inactivity");
          }
        }, SESSION_PAUSE_TIMEOUT_MS),
      );
    };
  }, [jobId]);

  /* Render */
  // Paused state belongs to an unmounted tab; consumers only see a live one.
  const isLive = status === "live";
  return {
    getSessionProgress,
    goals: isLive ? goals : null,
    interestCoins,
    interestCount,
    recordInterest,
    recordInterestCoins,
    recordShortlist,
    session: isLive ? session : null,
    shortlists,
    stats: isLive ? stats : null,
  };
};
